import { useState, useCallback } from 'react';
import { trackEvent } from '../services/analytics';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Hook for managing export creation and status polling
 */
export function useExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Create a new export
   * @param {string[]} clipIds - Array of clip IDs to export
   * @param {string} template - Template type (default: 'default')
   */
  const createExport = useCallback(async (clipIds, template = 'default') => {
    setIsExporting(true);
    setError(null);
    setExportStatus(null);

    try {
      const res = await fetch(`${API_BASE}/api/exports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clipIds, template }),
      });

      if (!res.ok) {
        let message = 'Failed to create export';
        try {
          const data = await res.json();
          message = data?.message || data?.error?.message || message;
        } catch (e) {
          // ignore parse errors and use fallback message
        }
        throw new Error(message);
      }

      const data = await res.json();
      setExportStatus(data.export);
      return data.export;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Poll export status
   * @param {string} exportId - Export ID to check
   */
  const checkStatus = useCallback(async (exportId) => {
    try {
      const res = await fetch(`${API_BASE}/api/exports/${exportId}`, {
        credentials: 'include',
      });

      if (!res.ok) {
        let message = 'Failed to check status';
        try {
          const data = await res.json();
          message = data?.message || data?.error?.message || message;
        } catch (e) {
          // ignore parse errors and use fallback message
        }
        throw new Error(message);
      }

      const data = await res.json();
      setExportStatus(data.export);
      return data.export;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Start polling for export completion
   * @param {string} exportId - Export ID to poll
   * @param {number} interval - Poll interval in ms (default: 1000)
   */
  const pollUntilComplete = useCallback(async (exportId, interval = 1000) => {
    const poll = async () => {
      const status = await checkStatus(exportId);
      
      if (status.status === 'completed' || status.status === 'failed') {
        setIsExporting(false);
        return status;
      }

      // Continue polling
      await new Promise((resolve) => setTimeout(resolve, interval));
      return poll();
    };

    return poll();
  }, [checkStatus]);

  /**
   * Trigger download of completed export
   * @param {string} exportId - Export ID
   * @param {string} token - Download token
   */
  const downloadExport = useCallback((exportId, token) => {
    const url = `${API_BASE}/api/exports/${exportId}/download?token=${token}`;
    
    // Create hidden link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `clips-export-${exportId}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    try {
      // Fire-and-forget analytics event to record export download
      // Do not block or throw if analytics fails
      trackEvent('html_exported', { export_id: exportId });
    } catch (e) {
      // swallow analytics errors
    }
  }, []);

  /**
   * Reset export state
   */
  const reset = useCallback(() => {
    setIsExporting(false);
    setExportStatus(null);
    setError(null);
  }, []);

  return {
    isExporting,
    exportStatus,
    error,
    createExport,
    checkStatus,
    pollUntilComplete,
    downloadExport,
    reset,
  };
}
